import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import type { Client } from 'api';
import { MoreHorizontal, Pencil } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable, TableScrollBox } from 'shared/components/DataTable';
import { HeroContainer } from 'shared/components/HeroContainer';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  ActionCell,
  ApplyButton,
  ClearFiltersLink,
  CLIENTS_MENU_ITEM_SX,
  CLIENTS_MENU_PAPER_SX,
  CLIENTS_MENU_TEXT_SX,
  ClientNameCell,
  ClientNameText,
  ClientRowArrow,
  Container,
  ClientProjectStatusPill,
  DeleteMenuItem,
  FilterSelect,
  FilterToolbarCard,
  HeroAddButton,
  HeroBannerButtonBox,
  HeroBannerRow,
  HeroBannerTextBox,
  HeroSubtitle,
  HeroTitle,
  SearchFieldBox,
  SearchIconWrap,
  SearchInput,
  STATUS_MENU_ITEM_SX,
  STATUS_MENU_LIST_SX,
  STATUS_MENU_PAPER_SX,
} from '../clients.styles';
import { useClientActions } from '../hooks/useClientActions';

import { ClientFormModal } from './ClientFormModal';

type StatusFilter = 'Active' | 'Inactive' | 'OnHold' | 'all';

interface RowMenuState {
  anchorEl: HTMLElement | null;
  clientId: string | null;
}

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { canCreateClient, canEditClient, canDeleteClient } = usePermissions();
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<StatusFilter>('all');
  const [pendingStatusFilter, setPendingStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  // const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowMenu, setRowMenu] = useState<RowMenuState>({ anchorEl: null, clientId: null });

  const {
    clients,
    isLoading,
    clientModal,
    openCreateModal,
    openEditModal,
    closeModal,
    handleModalSubmit,
    createMutation,
    updateMutation,
    statusDialog,
    confirmStatusChange,
    cancelStatusChange,
    deleteDialog,
    requestDelete,
    confirmDelete,
    cancelDelete,
    deleteMutation,
  } = useClientActions();

  const filteredClients = useMemo(() => {
    return clients?.filter((client) => {
      const matchesStatus = appliedStatusFilter === 'all' || client.status === appliedStatusFilter;
      const matchesSearch =
        !appliedSearch || client.name.toLowerCase().includes(appliedSearch.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [clients, appliedStatusFilter, appliedSearch]);

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, clientId: string) => {
    setRowMenu({ anchorEl: event.currentTarget, clientId });
  };

  const closeRowMenu = () => {
    setRowMenu({ anchorEl: null, clientId: null });
  };

  const handleEditClick = (clientId: string) => {
    openEditModal(clientId);
  };

  const hasFilters = appliedStatusFilter !== 'all' || appliedSearch !== '';

  const handleClearFilters = () => {
    setPendingStatusFilter('all');
    setSearchTerm('');
    setAppliedStatusFilter('all');
    setAppliedSearch('');
  };

  const handleApplyFilters = () => {
    setAppliedStatusFilter(pendingStatusFilter);
    setAppliedSearch(searchTerm);
  };

  const handleDeleteClick = () => {
    if (!rowMenu.clientId) return;
    requestDelete(rowMenu.clientId);
    closeRowMenu();
  };

  const columns: DataTableColumn<Client>[] = [
    {
      id: 'name',
      label: 'NAME',
      render: (client) => (
        <ClientNameCell>
          <ClientNameText>{client.name}</ClientNameText>
          <ClientRowArrow size={14} />
        </ClientNameCell>
      ),
    },
    {
      id: 'mappedMemberCount',
      label: 'MAPPED MEMBERS',
      width: 200,
      align: 'center',
      render: (client) => client.mappedMemberCount,
    },
    {
      id: 'activeMappedMemberCount',
      label: 'ACTIVE MAPPED MEMBERS',
      width: 200,
      align: 'center',
      render: (client) => client.activeMappedMemberCount,
    },
    {
      id: 'activeContributorCount',
      label: 'ACTIVE CONTRIBUTORS',
      width: 200,
      align: 'center',
      render: (client) => client.activeContributorCount,
    },
    {
      id: 'exitedMemberCount',
      label: 'EXITED MEMBERS',
      width: 200,
      align: 'center',
      render: (client) => client.exitedMemberCount,
    },
    {
      id: 'status',
      label: 'STATUS',
      width: 110,
      render: (client) => {
        const s = client.status.toLowerCase() as 'active' | 'onhold' | 'inactive';
        const statusKey = s === 'inactive' ? 'archived' : s;
        return (
          <ClientProjectStatusPill status={statusKey}>
            {client.status === 'OnHold' ? 'On Hold' : client.status}
          </ClientProjectStatusPill>
        );
      },
    },
    {
      id: 'actions',
      label: '',
      width: 100,
      align: 'right',
      render: (client) => (
        <ActionCell>
          {canEditClient && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(client.id);
                }}
              >
                <Pencil size={14} />
              </IconButton>
            </Tooltip>
          )}
          {(canEditClient || canDeleteClient) && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openRowMenu(e, client.id);
              }}
            >
              <MoreHorizontal size={14} />
            </IconButton>
          )}
        </ActionCell>
      ),
    },
  ];

  return (
    <Container>
      {/* Hero banner */}
      <HeroContainer padding="24px 28px" style={{ marginBottom: 20 }}>
        <HeroBannerRow>
          <HeroBannerTextBox>
            <HeroTitle>Clients</HeroTitle>
            <HeroSubtitle>Manage clients and their mapped members</HeroSubtitle>
          </HeroBannerTextBox>
          {canCreateClient && (
            <HeroBannerButtonBox>
              <HeroAddButton onClick={openCreateModal}>+ New client</HeroAddButton>
            </HeroBannerButtonBox>
          )}
        </HeroBannerRow>
      </HeroContainer>

      {/* Filter toolbar */}
      <FilterToolbarCard>
        <SearchFieldBox>
          <SearchIconWrap>
            <SearchIcon sx={{ fontSize: 16 }} />
          </SearchIconWrap>
          <SearchInput
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchFieldBox>

        <FilterSelect
          value={pendingStatusFilter}
          onChange={(e) => setPendingStatusFilter(e.target.value as StatusFilter)}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            PaperProps: { sx: STATUS_MENU_PAPER_SX },
            MenuListProps: { sx: STATUS_MENU_LIST_SX },
          }}
        >
          {[
            { v: 'all', label: 'All' },
            { v: 'Active', label: 'Active' },
            { v: 'Inactive', label: 'Inactive' },
            { v: 'OnHold', label: 'On Hold' },
          ].map(({ v, label }) => (
            <MenuItem key={v} value={v} sx={STATUS_MENU_ITEM_SX}>
              {label}
            </MenuItem>
          ))}
        </FilterSelect>

        {hasFilters && (
          <ClearFiltersLink type="button" onClick={handleClearFilters}>
            Clear filters
          </ClearFiltersLink>
        )}

        <ApplyButton variant="contained" disableElevation onClick={handleApplyFilters}>
          Apply
        </ApplyButton>
      </FilterToolbarCard>

      <TableScrollBox>
        <DataTable<Client>
          columns={columns}
          rows={filteredClients}
          getRowId={(client) => client.id}
          onRowClick={(id) => navigate(ROUTE_PATHS.CLIENT_DETAIL(id))}
          emptyMessage={isLoading ? 'Loading clients…' : 'No clients match the current filter.'}
          borderless
          headerBgColor="#F3F0FF"
          headerColor="#5A5578"
          headerWeight={500}
          headerFontSize="11px"
          rowHeight={56}
          rowHoverColor="rgba(110, 79, 247, 0.03)"
        />
      </TableScrollBox>

      {/* Row kebab menu */}
      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: CLIENTS_MENU_PAPER_SX } }}
      >
        {canDeleteClient && (
          <DeleteMenuItem sx={CLIENTS_MENU_ITEM_SX} onClick={handleDeleteClick}>
            <ListItemText slotProps={{ primary: { sx: CLIENTS_MENU_TEXT_SX } }}>
              Delete
            </ListItemText>
          </DeleteMenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => confirmDelete()}
        onCancel={cancelDelete}
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={statusDialog.open}
        title="Set Client Inactive"
        message={`Setting "${clients.find((c) => c.id === statusDialog.clientId)?.name ?? 'this client'}" to inactive will also set all projects under it to inactive. Are you sure you want to proceed?`}
        confirmLabel="Set Inactive"
        onConfirm={confirmStatusChange}
        onCancel={cancelStatusChange}
        loading={updateMutation.isPending}
      />

      <ClientFormModal
        open={clientModal.open}
        client={
          clientModal.clientId ? (clients.find((c) => c.id === clientModal.clientId) ?? null) : null
        }
        initialName={clientModal.initialName}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
      />
    </Container>
  );
};

export default ClientsPage;
